/**
 * activity-log-store.ts — Activity log store for AIP.
 *
 * BUILD-13 Item 6: Primary write path is Supabase (via /api/me/activity/daily).
 * localStorage is kept as a same-session read cache only — cleared by backfill.ts
 * on next login after the migration.
 *
 * Storage keys (cache only — not source of truth post-migration):
 *   BDR: aip:activity-log:bdr:{user_id}:{YYYY-MM-DD}
 *        aip:activity-log:bdr:index
 *   AE:  aip:activity-log:ae:{user_id}:{YYYY-MM-DD}
 *        aip:activity-log:ae:index
 *
 * Shape per entry:
 *   { user_id, user_name, role, date, fields: {...}, notes, saved_at }
 */

import { api } from "@/lib/session";

// ─── Types ────────────────────────────────────────────────────────────────────

export interface BdrFields {
  // Phone activity
  dialsMade: number;
  liveConversations: number;
  voicemailsLeft: number;
  noMessageHangups: number;
  // Email / LinkedIn
  emailsSent: number;
  linkedInDMs: number;
  emailRepliesReceived: number;
  // Outcomes
  suspectsQualified: number;
  prospectsQualified: number;
  meetingsBooked: number;
  meetingsHeld: number;
  noShows: number;
}

export interface AeFields {
  // Account-touching totals
  callsMade: number;
  emailsSent: number;
  meetingsHeld: number;
  demosGiven: number;
  proposalsSent: number;
  contractsSent: number;
  dealsAdvanced: number;
}

export interface AeAccountEntry {
  id: string;
  accountName: string;
  account_id: number | null;  // Bug B fix: resolved FK id from account picker (null = unresolved)
  activityType: "Call" | "Email" | "Meeting" | "Demo" | "Proposal" | "Contract" | "Other";
  outcomeNote: string;
}

export interface BdrLog {
  user_id: string;
  user_name: string;
  role: "bdr";
  date: string; // YYYY-MM-DD
  fields: BdrFields;
  notes: string;
  saved_at: string; // ISO timestamp
}

export interface AeLog {
  user_id: string;
  user_name: string;
  role: "ae";
  date: string;
  fields: AeFields;
  accountEntries: AeAccountEntry[];
  notes: string;
  saved_at: string;
}

// ─── Index types ──────────────────────────────────────────────────────────────

interface BdrIndex {
  entries: Array<{ user_id: string; date: string }>;
}

interface AeIndex {
  entries: Array<{ user_id: string; date: string }>;
}

// ─── Date helpers ─────────────────────────────────────────────────────────────

export function todayString(): string {
  return new Date().toISOString().slice(0, 10);
}

export function weekStartString(date?: string): string {
  const d = date ? new Date(date) : new Date();
  const day = d.getDay(); // 0=Sun
  const diff = day === 0 ? -6 : 1 - day; // Monday
  d.setDate(d.getDate() + diff);
  return d.toISOString().slice(0, 10);
}

function addDays(dateStr: string, n: number): string {
  const d = new Date(dateStr);
  d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
}

// ─── BDR read/write ───────────────────────────────────────────────────────────

/**
 * saveBdrLog — BUILD-13 Item 6 patched.
 * Primary write: Supabase via /api/me/activity/daily (all 12 BdrFields).
 * Secondary write: localStorage cache for same-session reads.
 * On API failure, still caches locally so the rep does not lose work;
 * backfill.ts will retry on next login.
 */
export async function saveBdrLog(log: BdrLog): Promise<void> {
  // 1. Primary: write all 12 fields to Supabase
  try {
    await api("/api/me/activity/daily", {
      method: "PUT",
      body: JSON.stringify({
        activity_date:          log.date,
        outreach:               log.fields.dialsMade,
        first_meetings:         log.fields.meetingsBooked,
        qualified_meetings:     log.fields.meetingsHeld,
        qualified_handoffs:     log.fields.prospectsQualified,
        suspects_qualified:     log.fields.suspectsQualified,
        live_conversations:     log.fields.liveConversations,
        voicemails_left:        log.fields.voicemailsLeft,
        no_message_hangups:     log.fields.noMessageHangups,
        emails_sent:            log.fields.emailsSent,
        linkedin_dms:           log.fields.linkedInDMs,
        email_replies_received: log.fields.emailRepliesReceived,
        no_shows:               log.fields.noShows,
        notes:                  log.notes || null,
        source:                 "self",
      }),
    });
  } catch (err: any) {
    console.error("[activity-log-store] Supabase write failed — caching locally:", err?.message);
  }

  // 2. Cache locally for same-session reads (not cross-device source of truth)
  const key = `aip:activity-log:bdr:${log.user_id}:${log.date}`;
  try {
    localStorage.setItem(key, JSON.stringify(log));
    const idxKey = "aip:activity-log:bdr:index";
    const raw = localStorage.getItem(idxKey);
    const idx: BdrIndex = raw ? JSON.parse(raw) : { entries: [] };
    const exists = idx.entries.some(
      (e) => e.user_id === log.user_id && e.date === log.date
    );
    if (!exists) {
      idx.entries.push({ user_id: log.user_id, date: log.date });
      localStorage.setItem(idxKey, JSON.stringify(idx));
    }
  } catch {
    // Quota exceeded or private mode — server write is the source of truth.
  }
}

export function getBdrLog(user_id: string, date: string): BdrLog | null {
  const key = `aip:activity-log:bdr:${user_id}:${date}`;
  const raw = localStorage.getItem(key);
  return raw ? (JSON.parse(raw) as BdrLog) : null;
}

/**
 * getBdrLogFromApi — fetches a single BDR daily log row from the server.
 * Maps daily_activity_actual columns back to BdrFields.
 * Returns null if no row exists for the given date.
 */
export async function getBdrLogFromApi(
  user_id: string,
  user_name: string,
  date: string,
): Promise<BdrLog | null> {
  try {
    const data = await api<{ row: any }>(`/api/me/activity/daily?date=${date}`);
    const row = data?.row;
    if (!row) return null;
    const log: BdrLog = {
      user_id,
      user_name,
      role: "bdr",
      date,
      fields: {
        dialsMade:             Number(row.outreach               ?? 0),
        liveConversations:     Number(row.live_conversations     ?? 0),
        voicemailsLeft:        Number(row.voicemails_left        ?? 0),
        noMessageHangups:      Number(row.no_message_hangups     ?? 0),
        emailsSent:            Number(row.emails_sent            ?? 0),
        linkedInDMs:           Number(row.linkedin_dms           ?? 0),
        emailRepliesReceived:  Number(row.email_replies_received ?? 0),
        suspectsQualified:     Number(row.suspects_qualified    ?? 0),
        prospectsQualified:    Number(row.qualified_handoffs     ?? 0),
        meetingsBooked:        Number(row.first_meetings         ?? 0),
        meetingsHeld:          Number(row.qualified_meetings     ?? 0),
        noShows:               Number(row.no_shows               ?? 0),
      },
      notes:    row.notes ?? "",
      saved_at: row.updated_at ?? new Date().toISOString(),
    };
    // Backfill localStorage cache from API row so offline reads work
    const key = `aip:activity-log:bdr:${user_id}:${date}`;
    try { localStorage.setItem(key, JSON.stringify(log)); } catch { /* quota */ }
    return log;
  } catch {
    return null;
  }
}

export function getBdrActivityForDay(user_id: string, date: string): BdrLog | null {
  return getBdrLog(user_id, date);
}

export function getBdrActivityForWeek(
  user_id: string,
  week_start?: string
): { fields: BdrFields; daysWithData: number } {
  const start = week_start ?? weekStartString();
  const empty: BdrFields = {
    dialsMade: 0, liveConversations: 0, voicemailsLeft: 0, noMessageHangups: 0,
    emailsSent: 0, linkedInDMs: 0, emailRepliesReceived: 0,
    suspectsQualified: 0, prospectsQualified: 0,
    meetingsBooked: 0, meetingsHeld: 0, noShows: 0,
  };
  let daysWithData = 0;

  for (let i = 0; i < 7; i++) {
    const date = addDays(start, i);
    const log = getBdrLog(user_id, date);
    if (log) {
      daysWithData++;
      (Object.keys(empty) as Array<keyof BdrFields>).forEach((k) => {
        (empty[k] as number) += log.fields[k] ?? 0;
      });
    }
  }
  return { fields: empty, daysWithData };
}

export function getBdrTeamActivityForDay(date: string): {
  logs: BdrLog[];
  totals: BdrFields;
} {
  const idxKey = "aip:activity-log:bdr:index";
  const raw = localStorage.getItem(idxKey);
  const idx: BdrIndex = raw ? JSON.parse(raw) : { entries: [] };

  const logs: BdrLog[] = [];
  const totals: BdrFields = {
    dialsMade: 0, liveConversations: 0, voicemailsLeft: 0, noMessageHangups: 0,
    emailsSent: 0, linkedInDMs: 0, emailRepliesReceived: 0,
    suspectsQualified: 0, prospectsQualified: 0,
    meetingsBooked: 0, meetingsHeld: 0, noShows: 0,
  };

  idx.entries
    .filter((e) => e.date === date)
    .forEach((e) => {
      const log = getBdrLog(e.user_id, e.date);
      if (log) {
        logs.push(log);
        (Object.keys(totals) as Array<keyof BdrFields>).forEach((k) => {
          (totals[k] as number) += log.fields[k] ?? 0;
        });
      }
    });

  return { logs, totals };
}

// ─── AE read/write ────────────────────────────────────────────────────────────

export function saveAeLog(log: AeLog): void {
  const key = `aip:activity-log:ae:${log.user_id}:${log.date}`;
  localStorage.setItem(key, JSON.stringify(log));

  const idxKey = "aip:activity-log:ae:index";
  const raw = localStorage.getItem(idxKey);
  const idx: AeIndex = raw ? JSON.parse(raw) : { entries: [] };
  const exists = idx.entries.some(
    (e) => e.user_id === log.user_id && e.date === log.date
  );
  if (!exists) {
    idx.entries.push({ user_id: log.user_id, date: log.date });
    localStorage.setItem(idxKey, JSON.stringify(idx));
  }
}

export function getAeLog(user_id: string, date: string): AeLog | null {
  const key = `aip:activity-log:ae:${user_id}:${date}`;
  const raw = localStorage.getItem(key);
  return raw ? (JSON.parse(raw) as AeLog) : null;
}

/**
 * getAeLogFromApi — fetches a single AE daily log row from the server.
 * Maps daily_activity_actual columns back to AeFields.
 * Account entries are preserved from localStorage cache (not stored server-side as rows).
 * Returns null if no row exists for the given date.
 */
export async function getAeLogFromApi(
  user_id: string,
  user_name: string,
  date: string,
): Promise<AeLog | null> {
  try {
    const data = await api<{ row: any }>(`/api/me/activity/daily?date=${date}`);
    const row = data?.row;
    if (!row) return null;
    // Restore account entries from localStorage (not persisted as columns server-side)
    const cached = getAeLog(user_id, date);
    const log: AeLog = {
      user_id,
      user_name,
      role: "ae",
      date,
      fields: {
        callsMade:      Number(row.outreach            ?? 0),
        emailsSent:     Number(row.emails_sent         ?? 0),
        meetingsHeld:   Number(row.first_meetings      ?? 0),
        demosGiven:     Number(row.qualified_meetings  ?? 0),
        // Fix 2: read from dedicated columns (primary source of truth after 2026-05-20 migration)
        proposalsSent:  Number(row.proposals_sent      ?? 0),
        contractsSent:  Number(row.contracts_sent      ?? 0),
        dealsAdvanced:  Number(row.closed_won_count    ?? 0),
      },
      accountEntries: cached?.accountEntries ?? [],
      notes:          row.notes ?? "",
      saved_at:       row.updated_at ?? new Date().toISOString(),
    };
    // Defense-in-depth: if new columns returned 0 but localStorage has non-zero values,
    // prefer localStorage (handles rows written before the 2026-05-20 migration).
    // This fallback is intentionally retained for one release cycle.
    if (cached) {
      if (log.fields.proposalsSent === 0 && cached.fields.proposalsSent > 0) {
        log.fields.proposalsSent = cached.fields.proposalsSent;
      }
      if (log.fields.contractsSent === 0 && cached.fields.contractsSent > 0) {
        log.fields.contractsSent = cached.fields.contractsSent;
      }
    }
    // Backfill localStorage cache from API row so offline reads work
    const key = `aip:activity-log:ae:${user_id}:${date}`;
    try { localStorage.setItem(key, JSON.stringify(log)); } catch { /* quota */ }
    return log;
  } catch {
    return null;
  }
}

export function getAeActivityForDay(user_id: string, date: string): AeLog | null {
  return getAeLog(user_id, date);
}

export function getAeActivityForWeek(
  user_id: string,
  week_start?: string
): { fields: AeFields; daysWithData: number } {
  const start = week_start ?? weekStartString();
  const empty: AeFields = {
    callsMade: 0, emailsSent: 0, meetingsHeld: 0,
    demosGiven: 0, proposalsSent: 0, contractsSent: 0, dealsAdvanced: 0,
  };
  let daysWithData = 0;

  for (let i = 0; i < 7; i++) {
    const date = addDays(start, i);
    const log = getAeLog(user_id, date);
    if (log) {
      daysWithData++;
      (Object.keys(empty) as Array<keyof AeFields>).forEach((k) => {
        (empty[k] as number) += log.fields[k] ?? 0;
      });
    }
  }
  return { fields: empty, daysWithData };
}

export function getAeTeamActivityForDay(date: string): {
  logs: AeLog[];
  totals: AeFields;
} {
  const idxKey = "aip:activity-log:ae:index";
  const raw = localStorage.getItem(idxKey);
  const idx: AeIndex = raw ? JSON.parse(raw) : { entries: [] };

  const logs: AeLog[] = [];
  const totals: AeFields = {
    callsMade: 0, emailsSent: 0, meetingsHeld: 0,
    demosGiven: 0, proposalsSent: 0, contractsSent: 0, dealsAdvanced: 0,
  };

  idx.entries
    .filter((e) => e.date === date)
    .forEach((e) => {
      const log = getAeLog(e.user_id, e.date);
      if (log) {
        logs.push(log);
        (Object.keys(totals) as Array<keyof AeFields>).forEach((k) => {
          (totals[k] as number) += log.fields[k] ?? 0;
        });
      }
    });

  return { logs, totals };
}

// ─── Default empty fields ─────────────────────────────────────────────────────

export function emptyBdrFields(): BdrFields {
  return {
    dialsMade: 0, liveConversations: 0, voicemailsLeft: 0, noMessageHangups: 0,
    emailsSent: 0, linkedInDMs: 0, emailRepliesReceived: 0,
    suspectsQualified: 0, prospectsQualified: 0,
    meetingsBooked: 0, meetingsHeld: 0, noShows: 0,
  };
}

export function emptyAeFields(): AeFields {
  return {
    callsMade: 0, emailsSent: 0, meetingsHeld: 0,
    demosGiven: 0, proposalsSent: 0, contractsSent: 0, dealsAdvanced: 0,
  };
}

// ─── Generic activity-window query (Bryan's doctrine: 5 ways to view raw rows)
//
// All raw rows in localStorage remain untouched. These are read-only filters
// over the activity log. Falls back to deterministic mock baselines when the
// underlying store is sparse so the demo always tells a coherent story.
// ─────────────────────────────────────────────────────────────────────────────

export type ActivityWindow = 'today' | 'week' | 'month' | 'quarter' | 'year';

export interface ActivityWindowResult {
  dials: number;
  connects: number;
  meetings: number;
  sparklineBuckets: number[];
  previousPeriod: {
    dials: number;
    connects: number;
    meetings: number;
  };
}

// Bucket count per window — matches spec exactly
function bucketCount(window: ActivityWindow): number {
  switch (window) {
    case 'today':   return 24;  // hourly
    case 'week':    return 7;   // daily
    case 'month':   return 5;   // weekly (ISO weeks-of-month, up to 5)
    case 'quarter': return 3;   // monthly
    case 'year':    return 12;  // monthly
  }
}

export function bucketUnit(window: ActivityWindow): 'hr' | 'day' | 'wk' | 'mo' {
  switch (window) {
    case 'today':   return 'hr';
    case 'week':    return 'day';
    case 'month':   return 'wk';
    case 'quarter': return 'mo';
    case 'year':    return 'mo';
  }
}

export function previousPeriodLabel(window: ActivityWindow): string {
  switch (window) {
    case 'today':   return 'yesterday';
    case 'week':    return 'last wk';
    case 'month':   return 'last mo';
    case 'quarter': return 'last qtr';
    case 'year':    return 'last yr';
  }
}

// Mock baselines per Bryan's spec. Week numbers preserve existing demo values.
const MOCK_BASELINES: Record<
  ActivityWindow,
  Record<'bdr' | 'ae', { dials: number; connects: number; meetings: number }>
> = {
  today:   { bdr: { dials: 28,   connects: 9,    meetings: 1   }, ae: { dials: 28,   connects: 9,    meetings: 1   } },
  week:    { bdr: { dials: 143,  connects: 46,   meetings: 9   }, ae: { dials: 143,  connects: 46,   meetings: 9   } },
  month:   { bdr: { dials: 580,  connects: 188,  meetings: 36  }, ae: { dials: 580,  connects: 188,  meetings: 36  } },
  quarter: { bdr: { dials: 1740, connects: 564,  meetings: 108 }, ae: { dials: 1740, connects: 564,  meetings: 108 } },
  year:    { bdr: { dials: 5800, connects: 1880, meetings: 360 }, ae: { dials: 5800, connects: 1880, meetings: 360 } },
};

// Deterministic shape for the sparkline — looks organic, fills only up to "now"
function sparklineShape(window: ActivityWindow, total: number): number[] {
  const n = bucketCount(window);

  // "now" cutoff per window (in bucket index, 0-based, exclusive end)
  const now = new Date();
  let filled = n;
  if (window === 'today') {
    filled = now.getHours() + 1; // hours so far today
  } else if (window === 'week') {
    const dow = now.getDay(); // 0=Sun..6=Sat
    filled = dow === 0 ? 7 : dow; // Mon=1..Sun=7
  } else if (window === 'month') {
    // ISO week of month, capped at 5
    const dom = now.getDate();
    filled = Math.min(Math.ceil(dom / 7), 5);
  } else if (window === 'quarter') {
    const monthInQ = now.getMonth() % 3; // 0,1,2
    filled = monthInQ + 1;
  } else if (window === 'year') {
    filled = now.getMonth() + 1; // months so far
  }
  filled = Math.max(1, Math.min(filled, n));

  // Distribute total across `filled` buckets with mild upward drift +- jitter.
  // Deterministic seed so re-renders don't jiggle.
  const buckets: number[] = [];
  const avg = total / Math.max(filled, 1);
  for (let i = 0; i < n; i++) {
    if (i >= filled) {
      buckets.push(0);
      continue;
    }
    // Drift: 0.75 → 1.15 across filled buckets
    const drift = 0.75 + (0.4 * i) / Math.max(filled - 1, 1);
    // Cheap deterministic jitter from sin
    const jitter = 0.92 + 0.16 * Math.abs(Math.sin((i + 1) * 7.13));
    buckets.push(Math.max(0, Math.round(avg * drift * jitter)));
  }
  return buckets;
}

// Window start date helpers
function windowStart(window: ActivityWindow, ref: Date = new Date()): Date {
  const d = new Date(ref);
  d.setHours(0, 0, 0, 0);
  if (window === 'today') return d;
  if (window === 'week') {
    const day = d.getDay();
    const diff = day === 0 ? -6 : 1 - day; // Monday
    d.setDate(d.getDate() + diff);
    return d;
  }
  if (window === 'month') {
    d.setDate(1);
    return d;
  }
  if (window === 'quarter') {
    const m = d.getMonth();
    const qStart = m - (m % 3);
    d.setMonth(qStart, 1);
    return d;
  }
  // year
  d.setMonth(0, 1);
  return d;
}

function previousWindowRange(window: ActivityWindow): { start: Date; end: Date } {
  const start = windowStart(window);
  if (window === 'today') {
    const prevEnd = new Date(start);
    const prevStart = new Date(start);
    prevStart.setDate(prevStart.getDate() - 1);
    return { start: prevStart, end: prevEnd };
  }
  if (window === 'week') {
    const prevStart = new Date(start);
    prevStart.setDate(prevStart.getDate() - 7);
    return { start: prevStart, end: start };
  }
  if (window === 'month') {
    const prevStart = new Date(start);
    prevStart.setMonth(prevStart.getMonth() - 1);
    return { start: prevStart, end: start };
  }
  if (window === 'quarter') {
    const prevStart = new Date(start);
    prevStart.setMonth(prevStart.getMonth() - 3);
    return { start: prevStart, end: start };
  }
  // year
  const prevStart = new Date(start);
  prevStart.setFullYear(prevStart.getFullYear() - 1);
  return { start: prevStart, end: start };
}

// Sum raw activity rows from the localStorage log within a date range.
// Returns null if no rows found (so caller can fall back to mock baseline).
function sumRawRows(
  role: 'bdr' | 'ae',
  start: Date,
  end: Date
): { dials: number; connects: number; meetings: number; rowCount: number } {
  const idxKey = `aip:activity-log:${role}:index`;
  const raw = localStorage.getItem(idxKey);
  if (!raw) return { dials: 0, connects: 0, meetings: 0, rowCount: 0 };

  let dials = 0;
  let connects = 0;
  let meetings = 0;
  let rowCount = 0;

  try {
    const idx = JSON.parse(raw) as { entries: Array<{ user_id: string; date: string }> };
    for (const e of idx.entries) {
      const d = new Date(e.date + 'T00:00:00');
      if (d < start || d >= end) continue;
      if (role === 'bdr') {
        const log = getBdrLog(e.user_id, e.date);
        if (!log) continue;
        rowCount++;
        dials    += log.fields.dialsMade        ?? 0;
        connects += log.fields.liveConversations ?? 0;
        meetings += log.fields.meetingsBooked   ?? 0;
      } else {
        const log = getAeLog(e.user_id, e.date);
        if (!log) continue;
        rowCount++;
        dials    += log.fields.callsMade     ?? 0;
        connects += log.fields.emailsSent    ?? 0; // AE "connects" proxy = emails sent
        meetings += log.fields.meetingsHeld  ?? 0;
      }
    }
  } catch {
    return { dials: 0, connects: 0, meetings: 0, rowCount: 0 };
  }
  return { dials, connects, meetings, rowCount };
}

export function getActivityForWindow(
  role: 'bdr' | 'ae',
  window: ActivityWindow,
  opts?: { allowMockBaseline?: boolean },
): ActivityWindowResult {
  const baseline = MOCK_BASELINES[window][role];
  // Mock baselines should only render for the demo tenant. Real tenants must
  // see honest zeros until reps actually log activity. Caller passes
  // allowMockBaseline=true for tenant 2 (Mi6 Demo) only.
  const allowMock = opts?.allowMockBaseline === true;

  // Current window — try raw rows first, fall back to mock baseline only when
  // explicitly allowed; otherwise show zeros.
  const start = windowStart(window);
  const end = new Date(); // up to now
  const raw = sumRawRows(role, start, end);

  const fallbackDials    = allowMock ? baseline.dials    : 0;
  const fallbackConnects = allowMock ? baseline.connects : 0;
  const fallbackMeetings = allowMock ? baseline.meetings : 0;

  const dials    = raw.rowCount > 0 ? raw.dials    : fallbackDials;
  const connects = raw.rowCount > 0 ? raw.connects : fallbackConnects;
  const meetings = raw.rowCount > 0 ? raw.meetings : fallbackMeetings;

  // Previous period — for trend delta
  const prevRange = previousWindowRange(window);
  const prevRaw = sumRawRows(role, prevRange.start, prevRange.end);
  const prevDials    = prevRaw.rowCount > 0 ? prevRaw.dials    : (allowMock ? Math.round(baseline.dials    * 0.88) : 0);
  const prevConnects = prevRaw.rowCount > 0 ? prevRaw.connects : (allowMock ? Math.round(baseline.connects * 0.88) : 0);
  const prevMeetings = prevRaw.rowCount > 0 ? prevRaw.meetings : (allowMock ? Math.round(baseline.meetings * 0.88) : 0);

  return {
    dials,
    connects,
    meetings,
    sparklineBuckets: sparklineShape(window, dials),
    previousPeriod: {
      dials: prevDials,
      connects: prevConnects,
      meetings: prevMeetings,
    },
  };
}

// Per-metric sparkline shape for connects + meetings tiles
export function sparklineForMetric(window: ActivityWindow, metricTotal: number): number[] {
  return sparklineShape(window, metricTotal);
}
